import { thesisApiBase } from '@/lib/thesisPlatform'

export type ThesisApiUser = {
  id: string
  email: string
  displayName: string
  sub: {
    planId: string
    startedAt: string
    /** null = Token 不过期 */
    expiresAt: string | null
    remainingTokens: number
    orderId: string
  } | null
}

function apiRoot(): string {
  return thesisApiBase()
}

async function apiFetch<T>(
  path: string,
  opts: RequestInit & { token?: string | null } = {},
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string; code?: string }> {
  const base = apiRoot()
  if (!base) return { ok: false, status: 0, error: '未配置 VITE_THESIS_API_BASE', code: 'NO_API' }
  const { token, ...init } = opts
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)
  try {
    const res = await fetch(`${base}${path}`, { ...init, headers })
    const text = await res.text()
    let json: Record<string, unknown> = {}
    try {
      json = text ? JSON.parse(text) : {}
    } catch {
      json = { error: text }
    }
    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        error: String(json.error || res.statusText || '请求失败'),
        code: typeof json.code === 'string' ? json.code : undefined,
      }
    }
    return { ok: true, data: json as T }
  } catch (e) {
    return { ok: false, status: 0, error: e instanceof Error ? e.message : '网络错误' }
  }
}

export function isThesisCloudAuth(): boolean {
  return !!apiRoot()
}

export async function sendRegisterCode(email: string) {
  return apiFetch<{ ok: true; expiresInSec: number; cooldownSec: number; devLogged?: boolean }>(
    '/api/auth/send-register-code',
    { method: 'POST', body: JSON.stringify({ email }) },
  )
}

export async function registerAccount(args: {
  email: string
  password: string
  emailCode: string
  displayName?: string
}) {
  return apiFetch<{ ok: true; token: string; user: ThesisApiUser }>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(args),
  })
}

export async function loginAccount(args: { email: string; password: string }) {
  return apiFetch<{ ok: true; token: string; user: ThesisApiUser }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(args),
  })
}

export async function fetchMe(token: string) {
  return apiFetch<{ ok: true; user: ThesisApiUser }>('/api/auth/me', { token })
}

export async function deductQuotaRemote(token: string, tokens: number) {
  return apiFetch<{ ok: true; deducted: number; user: ThesisApiUser | null }>('/api/quota/deduct', {
    method: 'POST',
    token,
    body: JSON.stringify({ tokens }),
  })
}

export async function fetchCodepayStatus() {
  return apiFetch<{ ok: boolean; checkout: boolean; webhook: boolean; planMap: boolean }>(
    '/api/codepay/status',
  )
}

export async function fetchCheckoutUrl(args: {
  plan: string
  email: string
  returnUrl?: string
  type?: 'alipay' | 'wxpay'
}) {
  const q = new URLSearchParams({
    plan: args.plan,
    email: args.email,
    ...(args.returnUrl ? { return: args.returnUrl } : {}),
    ...(args.type ? { type: args.type } : {}),
  })
  return apiFetch<{ ok: true; url: string; outTradeNo: string }>(
    `/api/codepay/checkout-url?${q}`,
  )
}
